import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { useHttp } from "../../../hooks";
import { API_END_POINTS } from "../../../utils/constants";

import { AcceptedOfferData, FilterType, OfferAnalyticData, OfferAnalyticRef, OfferType, SentOutOfferData } from "../_lib/constants";
import ContentHeader from "./ContentHeader";
import OfferAnalyticCard from "./OfferAnalyticCard";
import moment from "moment";

const OfferStatusInitial: string[] = [
    OfferType?.pending,
    OfferType?.scheduled,
    OfferType?.completed,
    OfferType?.draft,
    OfferType?.rejected
]

interface OfferAnalyticsProps {
    title?: string;
    ref?: any
}

const OfferAnalytics: React.FC<OfferAnalyticsProps> = forwardRef(({ title = '' }, ref) => {

    const [filter, setFilter] = useState<string | null>(null);
    const [offerStatus, setOfferStatus] = useState<Array<string>>(OfferStatusInitial);
    const [dateFilter, setDateFilter] = useState<any>({
        from: null,
        to: null
    });
    const [offerAnalyticState, setOfferAnalyticsState] = useState<OfferAnalyticData | null>(null);
    const [offerFilterToday, setOfferFilterToday] = useState<boolean>(false);

    const [{ data: res, loading }, fetchOfferAccountDetails] = useHttp(
        {
            url: `${API_END_POINTS.getAnalyticsOfferCount}`,
            method: "POST",
        },
        { manual: true }
    );
    const [acceptedOfferState, setAcceptedOfferState] = useState<AcceptedOfferData | null>(null);
    const [{ data: resAccepted, loading: loadingAccepted }, fetchAcceptedOfferDetails] = useHttp(
        {
            url: `${API_END_POINTS.getAnalyticsCountOfferAcceptedCount}`,
            method: "GET",
        },
        { manual: true }
    );
    const [sentOutOfferState, setSentOutOfferState] = useState<SentOutOfferData | null>(null);
    const [{ data: resSentOut, loading: loadingSentOut }, fetchSentOutOfferDetails] = useHttp(
        {
            url: `${API_END_POINTS.getAnalyticsPendingOffersCount}`,
            method: "GET",
        },
        { manual: true }
    );

    const offerAnalyticSentOutRef = useRef<OfferAnalyticRef>(null);
    const offerAnalyticPendingRef = useRef<OfferAnalyticRef>(null);
    const offerAnalyticScheduledRef = useRef<OfferAnalyticRef>(null);
    const offerAnalyticAcceptedRef = useRef<OfferAnalyticRef>(null);
    const offerAnalyticCompletedRef = useRef<OfferAnalyticRef>(null);
    const offerAnalyticDraftRef = useRef<OfferAnalyticRef>(null);
    const offerAnalyticRejectedRef = useRef<OfferAnalyticRef>(null);

    const handleTodayClick = (offerType: string): void => {
        setFilter(FilterType?.today);
        setDateFilter({
            from: null,
            to: null
        });
        setOfferStatus([offerType]);
    }

    const handleDateRange = (dateRange: any, offerType: string): void => {
        setDateFilter({ ...dateRange });
        setFilter(null);
        setOfferStatus([offerType]);
    };

    const handleReset = (offerType: string): void => {
        setFilter(null);
        setDateFilter({
            from: null,
            to: null
        });
        setOfferStatus([offerType]);
    }

    const handleGlobalFilterReset = (): void => {
        if (!loading) {
            setFilter('');
            setOfferFilterToday(false);
            setOfferStatus(OfferStatusInitial);
            setDateFilter({
                from: null,
                to: null
            });
            offerAnalyticSentOutRef?.current?.resetFilter();
            offerAnalyticPendingRef?.current?.resetFilter();
            offerAnalyticCompletedRef?.current?.resetFilter();
            offerAnalyticAcceptedRef?.current?.resetFilter();
            offerAnalyticDraftRef?.current?.resetFilter();
            offerAnalyticRejectedRef?.current?.resetFilter();
            offerAnalyticScheduledRef?.current?.resetFilter();
        }
    };

    // Client Updates Global Filter for keep the flow use forwardRef
    useImperativeHandle(ref, () => ({
        toggleOfferTodayFilter: () => {
            setFilter(FilterType?.today);
            setOfferStatus(OfferStatusInitial);
            setOfferFilterToday(true);
        },
        toggleOfferDateRangeFilter: (from: string, to: string) => {
            setDateFilter({
                from: moment.utc(from, 'D/M/YYYY').toISOString(),
                to: moment.utc(to, 'D/M/YYYY').toISOString(),
            });
            setFilter(null);
            setOfferStatus(OfferStatusInitial);
            setOfferFilterToday(true);
        },
        toggleOfferReset: handleGlobalFilterReset,

    }));

    useEffect(() => {
        const isGetOnlyType = offerStatus.length === 1 && (offerStatus[0] === OfferType.accepted || offerStatus[0] === OfferType.sentOut);
        if (isGetOnlyType) {
            return;
        }
        fetchOfferAccountDetails({
            data: {
                status: offerStatus,
                filter: filter,
                start_date: dateFilter?.from,
                end_date: dateFilter?.to
            }
        });
    }, [offerStatus, filter, dateFilter]);

    useEffect(() => {
        const offerType = offerStatus[0];
        const isGetOnlyType = offerType === OfferType.accepted || offerType === OfferType.sentOut;
        if (res && offerStatus.length === 5) {
            setOfferAnalyticsState({ ...res?.data });
        } else if (res && !isGetOnlyType) {
            setOfferAnalyticsState((prevState: any) => {
                return ({
                    ...prevState,
                    [`${offerType}_count`]: res?.data[`${offerType}_count`],
                    [`${offerType}_offer_amount_chf`]: res?.data[`${offerType}_offer_amount_chf`],
                    [`${offerType}_offer_amount_eur`]: res?.data[`${offerType}_offer_amount_eur`],
                    [`${offerType}_offer_amount_gbp`]: res?.data[`${offerType}_offer_amount_gbp`],
                });
            })
        }
    }, [res, offerStatus]);

    useEffect(() => {
        if (resAccepted) {
            setAcceptedOfferState({ ...resAccepted.data });
        }
    }, [resAccepted]);

    useEffect(() => {
        if (resSentOut) {
            setSentOutOfferState({ ...resSentOut.data });
        }
    }, [resSentOut]);

    const getOfferFilterParams = (asIsoDateTime = false) => {
        let filterParams = '';
        if (filter === FilterType.today) {
            filterParams = `?filter=${FilterType.today}`;
        } else if (dateFilter.from && dateFilter.to) {
            const startDate = asIsoDateTime ? moment.utc(dateFilter.from).toISOString() : dateFilter.from;
            const endDate = asIsoDateTime ? moment.utc(dateFilter.to).toISOString() : dateFilter.to;
            filterParams = `?start_date=${startDate}&end_date=${endDate}`;
        }
        return filterParams;
    }

    const handleAcceptedOfferFetch = () => {
        const filterParams = getOfferFilterParams();
        fetchAcceptedOfferDetails({
            url: `${API_END_POINTS.getAnalyticsCountOfferAcceptedCount}${filterParams}`
        });
    }

    const handleSentOutOfferFetch = () => {
        const filterParams = getOfferFilterParams(true);
        fetchSentOutOfferDetails({
            url: `${API_END_POINTS.getAnalyticsPendingOffersCount}${filterParams}`
        });
    }

    useEffect(() => {
        const isAcceptedFilter = offerStatus.length === 1 && offerStatus[0] === OfferType.accepted;
        const isSentOutFilter = offerStatus.length === 1 && offerStatus[0] === OfferType.sentOut;
        const isGlobalFilter = offerStatus.length > 1 || offerFilterToday;
        if (isAcceptedFilter || isGlobalFilter) {
            handleAcceptedOfferFetch();
        }
        if (isSentOutFilter || isGlobalFilter) {
            handleSentOutOfferFetch();
        }
    }, [filter, dateFilter, offerStatus, offerFilterToday]);

    return (
        <>
            <ContentHeader
                title='Offer analytics data'
                description={
                    <p>The dashboard displays key metrics of customers, artist and offers.
                        <br />
                        At the top of each section, you will find filters that allow you to set today or specific date ranges.
                    </p>
                }
            />
            <div className="row gy-4">
                <div className="col-lg-4 col-md-6 col-sm-12 col-12">
                    <OfferAnalyticCard
                        title="Offer sent out"
                        count={sentOutOfferState?.pending_offer_count}
                        onDateRangeChange={(dateRange: any) => handleDateRange(dateRange, OfferType?.sentOut)}
                        onTodayClick={handleTodayClick.bind(this, OfferType?.sentOut)}
                        amount_eur={sentOutOfferState?.pending_offer_amount_eur}
                        amount_chf={sentOutOfferState?.pending_offer_amount_chf}
                        amount_gbp={sentOutOfferState?.pending_offer_amount_gbp}
                        amount_sek={sentOutOfferState?.pending_offer_amount_sek}
                        amount_ron={sentOutOfferState?.pending_offer_amount_ron}
                        amount_pln={sentOutOfferState?.pending_offer_amount_pln}
                        amount_czk={sentOutOfferState?.pending_offer_amount_czk}
                        amount_huf={sentOutOfferState?.pending_offer_amount_huf}
                        onTriggerReset={handleReset.bind(this, OfferType?.sentOut)}
                        isLoading={loadingSentOut}
                        isGlobalToday={offerFilterToday}
                        ref={offerAnalyticSentOutRef}
                    />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12 col-12">
                    <OfferAnalyticCard
                        title="Offer Pending"
                        count={offerAnalyticState?.pending_count}
                        onDateRangeChange={(dateRange: any) => handleDateRange(dateRange, OfferType?.pending)}
                        onTodayClick={handleTodayClick.bind(this, OfferType?.pending)}
                        amount_chf={offerAnalyticState?.pending_offer_amount_chf}
                        amount_eur={offerAnalyticState?.pending_offer_amount_eur}
                        amount_gbp={offerAnalyticState?.pending_offer_amount_gbp}
                        onTriggerReset={handleReset.bind(this, OfferType?.pending)}
                        isLoading={offerStatus.includes(OfferType?.pending) && loading}
                        isGlobalToday={offerFilterToday}
                        ref={offerAnalyticPendingRef}
                    />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12 col-12">
                    <OfferAnalyticCard
                        title="Offer Scheduled"
                        count={offerAnalyticState?.scheduled_count}
                        onDateRangeChange={(dateRange: any) => handleDateRange(dateRange, OfferType?.scheduled)}
                        onTodayClick={handleTodayClick.bind(this, OfferType?.scheduled)}
                        amount_chf={offerAnalyticState?.scheduled_offer_amount_chf}
                        amount_eur={offerAnalyticState?.scheduled_offer_amount_eur}
                        amount_gbp={offerAnalyticState?.scheduled_offer_amount_gbp}
                        onTriggerReset={handleReset.bind(this, OfferType?.scheduled)}
                        isLoading={offerStatus.includes(OfferType?.scheduled) && loading}
                        isGlobalToday={offerFilterToday}
                        ref={offerAnalyticScheduledRef}
                    />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12 col-12">
                    <OfferAnalyticCard
                        title="Accepted Offer"
                        count={acceptedOfferState?.accepted_offer_count}
                        onDateRangeChange={(dateRange: any) => handleDateRange(dateRange, OfferType?.accepted)}
                        onTodayClick={handleTodayClick.bind(this, OfferType?.accepted)}
                        amount_eur={acceptedOfferState?.accepted_offer_amount_eur}
                        amount_chf={acceptedOfferState?.accepted_offer_amount_chf}
                        amount_gbp={acceptedOfferState?.accepted_offer_amount_gbp}
                        amount_sek={acceptedOfferState?.accepted_offer_amount_sek}
                        amount_ron={acceptedOfferState?.accepted_offer_amount_ron}
                        amount_pln={acceptedOfferState?.accepted_offer_amount_pln}
                        amount_czk={acceptedOfferState?.accepted_offer_amount_czk}
                        amount_huf={acceptedOfferState?.accepted_offer_amount_huf}
                        onTriggerReset={handleReset.bind(this, OfferType?.accepted)}
                        isLoading={loadingAccepted}
                        isGlobalToday={offerFilterToday}
                        currencyTitle="Highest offer"
                        ref={offerAnalyticAcceptedRef}
                    />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12 col-12">
                    <OfferAnalyticCard
                        title="Offer Completed"
                        count={offerAnalyticState?.completed_count}
                        onDateRangeChange={(dateRange: any) => handleDateRange(dateRange, OfferType?.completed)}
                        onTodayClick={handleTodayClick.bind(this, OfferType?.completed)}
                        amount_chf={offerAnalyticState?.completed_offer_amount_chf}
                        amount_eur={offerAnalyticState?.completed_offer_amount_eur}
                        amount_gbp={offerAnalyticState?.completed_offer_amount_gbp}
                        onTriggerReset={handleReset.bind(this, OfferType?.completed)}
                        isLoading={offerStatus.includes(OfferType?.completed) && loading}
                        isGlobalToday={offerFilterToday}
                        ref={offerAnalyticCompletedRef}
                    />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12 col-12">
                    <OfferAnalyticCard
                        title="Offer Draft"
                        count={offerAnalyticState?.draft_count}
                        onDateRangeChange={(dateRange: any) => handleDateRange(dateRange, OfferType?.draft)}
                        onTodayClick={handleTodayClick.bind(this, OfferType?.draft)}
                        amount_chf={offerAnalyticState?.draft_offer_amount_chf}
                        amount_eur={offerAnalyticState?.draft_offer_amount_eur}
                        amount_gbp={offerAnalyticState?.draft_offer_amount_gbp}
                        onTriggerReset={handleReset.bind(this, OfferType?.draft)}
                        isLoading={offerStatus.includes(OfferType?.draft) && loading}
                        isGlobalToday={offerFilterToday}
                        ref={offerAnalyticDraftRef}
                    />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12 col-12">
                    <OfferAnalyticCard
                        title="Offer Rejected"
                        count={offerAnalyticState?.rejected_count}
                        onDateRangeChange={(dateRange: any) => handleDateRange(dateRange, OfferType?.rejected)}
                        onTodayClick={handleTodayClick.bind(this, OfferType?.rejected)}
                        amount_chf={offerAnalyticState?.rejected_offer_amount_chf}
                        amount_eur={offerAnalyticState?.rejected_offer_amount_eur}
                        amount_gbp={offerAnalyticState?.rejected_offer_amount_gbp}
                        onTriggerReset={handleReset.bind(this, OfferType?.rejected)}
                        isLoading={offerStatus.includes(OfferType?.rejected) && loading}
                        isGlobalToday={offerFilterToday}
                        ref={offerAnalyticRejectedRef}
                    />
                </div>
            </div>
        </>
    )
});

export default OfferAnalytics;